import MatchBadge from './investigation/MatchBadge';
import ResultKeywordTags from './investigation/ResultKeywordTags';
import RiskBadge from './ui/RiskBadge';
import SourceAttribution from './investigation/SourceAttribution';
import HighlightedPhoneSnippet from './investigation/HighlightedPhoneSnippet';
import { FeedInspectButton, FeedInspectUrlLink } from './FeedInspectActions';
import {
  FEED_CARD_SHELL,
  FEED_CHIP,
  FEED_META,
  FEED_TITLE,
} from './investigation/feedCardStyles';
import { curateFeedBadges } from '../utils/resultKeywords';

/**
 * Unified dark-glass feed card used across Person, Email, Phone, and Corporate scans.
 *
 * @param {{
 *   res: object,
 *   title: string,
 *   icon?: string,
 *   avatarUrl?: string | null,
 *   shellClassName?: string,
 *   onInspect?: Function,
 *   isAlert?: boolean,
 *   children?: React.ReactNode,
 *   footer?: React.ReactNode,
 *   headerActions?: React.ReactNode,
 * }} props
 */
export default function FeedResultCard({
  res,
  title,
  icon = '🔎',
  avatarUrl = null,
  shellClassName = '',
  onInspect,
  isAlert = false,
  children = null,
  footer = null,
  headerActions = null,
}) {
  const safeTitle = String(title || '').trim() || 'Record';
  const matchedTags = curateFeedBadges(
    Array.isArray(res?.matched_tags) ? res.matched_tags : [],
    3,
  );
  const subdomains = Array.isArray(res?.details?.subdomains) ? res.details.subdomains : [];
  const phoneHint = res?.target || res?.details?.e164 || res?.phone || '';

  return (
    <article
      className={`${FEED_CARD_SHELL} ${shellClassName}`.trim()}
      data-feed-result="true"
    >
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
        <div className="flex min-w-0 flex-1 items-start gap-4 sm:gap-5">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-slate-900/80 shadow-inner">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <span className="text-2xl" aria-hidden>
                {icon}
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1 space-y-2">
            <div className="mb-0.5 flex flex-wrap items-center justify-between gap-2">
              <span className={`flex flex-wrap items-center gap-1 ${FEED_META} text-cyan-300`}>
                <SourceAttribution res={res} fallback="Intelligence record" />
                <RiskBadge isAlert={isAlert || Boolean(res?.error)} isHibp={res?.source === 'hibp'} />
              </span>
              <span className="text-[9px] font-bold text-slate-500">{new Date().toLocaleTimeString()}</span>
            </div>

            <div className="flex min-w-0 flex-col gap-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <h3
                  className={`${FEED_TITLE}${
                    isAlert ||
                    res?.ui_bucket === 'corporate_press' ||
                    res?.ui_bucket === 'negative_news' ||
                    res?.ui_bucket === 'sentiment_analysis'
                      ? ' line-clamp-2 leading-snug'
                      : ''
                  }`}
                >
                  {safeTitle}
                </h3>
                <MatchBadge value={res?.source_reliability ?? res?.score} />
              </div>

              {(matchedTags.length > 0 || subdomains.length > 0) ? (
                <div className="space-y-1.5" data-testid="feed-result-tech-badges">
                  {matchedTags.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1">
                      {matchedTags.map((tag) => (
                        <span key={tag} className={FEED_CHIP}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  {subdomains.length > 0 ? (
                    <div
                      className="max-h-52 overflow-y-auto rounded-lg border border-slate-800/60 bg-slate-950/40 p-2.5 pr-2 font-mono text-xs"
                      data-testid="feed-result-subdomains-scroll"
                    >
                      <ul className="m-0 list-none space-y-0.5 p-0">
                        {subdomains.slice(0, 80).map((subd) => (
                          <li key={subd} className="truncate text-cyan-300" title={subd}>
                            {subd}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            {res?.snippet ? (
              phoneHint ? (
                <HighlightedPhoneSnippet text={res.snippet} phoneRaw={phoneHint} />
              ) : (
                <p className="text-xs leading-relaxed text-slate-400 line-clamp-3">{res.snippet}</p>
              )
            ) : null}

            <ResultKeywordTags res={res} />

            <div className="flex flex-wrap items-center gap-1">
              <FeedInspectUrlLink res={res} fallback="Digital record" />
            </div>

            {children}
          </div>
        </div>

        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:items-end">
          {headerActions}
          <FeedInspectButton res={res} onInspect={onInspect} className="min-h-[44px] w-full sm:w-auto sm:min-h-0" />
        </div>
      </div>
      {footer ? <div className="mt-4 border-t border-white/5 pt-4">{footer}</div> : null}
    </article>
  );
}
